import PropTypes from "prop-types";
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Image,
  Slide,
  Slider,
} from "pure-react-carousel";
import { isEqual } from "lodash";

import SliderControls from "./SliderControls";
import { useEffect, useState } from "react";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Spinner } from "../Spinner";

const DetailViewCarousel = props => {
  const { imageList: propsImageList = [], actions, artworkId } = props;

  const [imageList, setImageList] = useState(propsImageList);
  const [successOpModal, setSuccessOpModal] = useState({
    isOpen: false,
    message: "",
  });

  const simplifyImageList = list =>
    list.map(img => ({
      archiveId: img.archiveId,
      order: img.order,
      description: img.description,
      channelDisplayOnline: img.channelDisplayOnline,
    }));

  useEffect(() => {
    if (
      !isEqual(simplifyImageList(imageList), simplifyImageList(propsImageList))
    ) {
      setImageList(propsImageList);
    }
  }, [propsImageList, imageList]);

  if (!imageList?.length) {
    return (
      <div className="flex h-[300px] w-full items-center justify-center rounded border border-dashed border-gray-300 text-sm text-gray-500">
        No images available
      </div>
    );
  }

  return (
    <div className="max-w-full overflow-x-auto">
      <CarouselProvider
        visibleSlides={6}
        totalSlides={imageList.length}
        naturalSlideWidth={275}
        naturalSlideHeight={300}
        hasMasterSpinner={true}
        touchEnabled={false}
        dragEnabled={false}
        className="flex w-full min-w-[700px] items-center justify-between lg:min-w-0"
      >
        <ButtonBack>
          <IconButton variant="clean" title="Back">
            <ChevronLeftIcon className={`h-5 w-5 cursor-pointer`} />
          </IconButton>
        </ButtonBack>
        <Slider className="w-11/12 gap-16" draggable={false}>
          {imageList?.map((image, index) => (
            <Slide
              index={index}
              key={image?.archiveId ?? `dvc_image_${index}`}
              className="group relative"
            >
              <CarouselImage
                index={index}
                image={image}
                imageList={imageList}
                artworkId={artworkId}
                actions={actions}
                setImageList={setImageList}
                setSuccessOpModal={setSuccessOpModal}
              />
            </Slide>
          ))}
        </Slider>
        <ButtonNext>
          <IconButton variant="clean" title="Next">
            <ChevronRightIcon className={`h-5 w-5 cursor-pointer`} />
          </IconButton>
        </ButtonNext>
        <Modal
          scale="sm"
          closeOnBackdrop={false}
          hideCloseButton={true}
          isOpen={successOpModal.isOpen}
          onClose={() => setSuccessOpModal({ isOpen: false, message: "" })}
          body={({ closeModal }) => (
            <div className="w-full">
              <div className="mb-5 text-lg font-medium leading-6 text-gray-900">
                {successOpModal.message}
              </div>
              <Button
                label={"Close"}
                onClick={() => {
                  closeModal();
                }}
              />
            </div>
          )}
        />
      </CarouselProvider>
    </div>
  );
};

DetailViewCarousel.propTypes = {
  imageList: PropTypes.array.isRequired,
};

DetailViewCarousel.defaultProps = {
  imageList: [],
};

const CarouselImage = props => {
  const {
    index,
    image,
    imageList,
    artworkId,
    actions,
    setImageList,
    setSuccessOpModal,
  } = props;

  const [imageLoadError, setImageLoadError] = useState(false);

  // Reset error state when image URL changes
  useEffect(() => {
    setImageLoadError(false);
  }, [image?.imgT]);

  // Show processing state if image was uploaded and either has no URL or failed to load
  const showProcessingState =
    image?.uploadedAt && (!image?.imgT || imageLoadError);

  return (
    <div className="grid h-full w-full grid-rows-6 gap-1 px-2 sm:px-4">
      <div
        className={`relative row-span-5 w-full group-hover:border ${index === 0 ? "bg-gray-200 px-4 py-3 " : ""}`}
      >
        {showProcessingState && (
          <div className="absolute inset-0">
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center">
              <Spinner className="!m-0" />
              <p className="text-xs">Processing, check back shortly</p>
            </div>
          </div>
        )}
        {image?.imgT && (
          <Image
            src={image.imgT}
            className={`box-border object-cover ${index === 0 ? "shadow-md shadow-gray-500" : ""}`}
            onError={() => {
              // Show processing state when image fails to load
              setImageLoadError(true);
            }}
          />
        )}
        <SliderControls
          imageIndex={index}
          images={imageList}
          artworkId={artworkId}
          actions={actions}
          setImageList={setImageList}
          setSuccessOpModal={setSuccessOpModal}
        />
      </div>
      <div
        className={`row-span-1 flex justify-${index === 0 ? "between" : "end"} block text-sm font-medium text-black`}
      >
        {index === 0 && <span>Primary</span>}
        {index + 1}
      </div>
    </div>
  );
};

export { DetailViewCarousel };
